import { useEffect, useMemo, useRef, useState } from 'react';
import TileCard from './TileCard';
import { getRecentCorrelations } from '../api/client';
import type { Env, RecentCorrelationItem, RecentCorrelations } from '../types';

const HELP =
  'Live list of correlation IDs (clientTrackingIds) seen in the workspace, ' +
  'newest first. Search by ID substring; filter by time window. Click an ID ' +
  'to load its full journey in the Correlation trace panel.';

const WINDOWS = [
  { label: 'Last 15 min', hours: 0.25 },
  { label: 'Last hour', hours: 1 },
  { label: 'Last 4 hours', hours: 4 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 168 },
] as const;

interface Props {
  env: Env;
  onSelect: (correlationId: string) => void;
  selectedCorrelationId?: string;
  // Refresh interval in seconds; null disables polling.
  pollSeconds?: number | null;
}

function formatRelative(iso: string): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function RecentTracesPanel({ env, onSelect, selectedCorrelationId, pollSeconds = 30 }: Props) {
  const [windowHours, setWindowHours] = useState<number>(4);
  const [search, setSearch] = useState('');
  const [workflowFilter, setWorkflowFilter] = useState<string>('');
  const [errorsOnly, setErrorsOnly] = useState<boolean>(false);
  const [data, setData] = useState<RecentCorrelations | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  // Effect: fetch + optionally poll
  useEffect(() => {
    let cancelled = false;
    const reqId = ++reqIdRef.current;
    const load = () => {
      setLoading(true);
      setError(null);
      // Strip whitespace from search and skip invalid chars
      const safe = search.replace(/[^A-Za-z0-9._:\-]/g, '');
      getRecentCorrelations(env, windowHours, safe || undefined)
        .then((d) => {
          if (cancelled || reqIdRef.current !== reqId) return;
          setData(d);
        })
        .catch((e: Error) => {
          if (cancelled || reqIdRef.current !== reqId) return;
          setError(e.message);
        })
        .finally(() => {
          if (!cancelled && reqIdRef.current === reqId) setLoading(false);
        });
    };
    load();
    if (pollSeconds && pollSeconds > 0) {
      const t = setInterval(load, pollSeconds * 1000);
      return () => {
        cancelled = true;
        clearInterval(t);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [env, windowHours, search, pollSeconds]);

  const allItems = data?.items ?? [];
  // Entry-workflow filter: hide syn-shared-* helper workflows from the
  // list view. The user only cares about the entry point (the syn-X
  // workflow APIM or the caller actually invoked); the shared helpers
  // are internal plumbing called downstream.
  const workflowOptions = useMemo(() => {
    const set = new Set<string>();
    for (const it of allItems) {
      for (const w of it.workflows) {
        if (!w.startsWith('syn-shared-')) set.add(w);
      }
    }
    return Array.from(set).sort();
  }, [allItems]);
  // Client-side filters:
  //   1. Drop items whose workflows are ALL syn-shared-* — these are orphan
  //      runs where the parent's CorrelationId variable fell through to
  //      workflow().run.name, so the shared workflow inherited a synthetic
  //      ID. The trace has no entry-workflow context worth surfacing in the
  //      list; the user can still find them via the full ID search if needed.
  //   2. Apply selected workflow + errors-only filters.
  const items = useMemo(() => {
    let filtered = allItems.filter((it) => it.workflows.some((w) => !w.startsWith('syn-shared-')));
    if (workflowFilter) filtered = filtered.filter((it) => it.workflows.includes(workflowFilter));
    if (errorsOnly) filtered = filtered.filter((it) => it.hasError);
    return filtered;
  }, [allItems, workflowFilter, errorsOnly]);
  const footer = data ? (
    <span>
      As of {new Date(data.generatedAt).toLocaleTimeString()} · {items.length}/{allItems.length} ID(s)
      {data.truncated && ' · truncated to 500'}
      {pollSeconds && pollSeconds > 0 && ` · auto-refresh ${pollSeconds}s`}
    </span>
  ) : undefined;

  return (
    <TileCard title={`Recent traces — ${env.toUpperCase()}`} help={HELP} loading={loading} error={error} footer={footer}>
      <form className="trace-form" onSubmit={(e) => e.preventDefault()}>
        <label className="wfrun-label">
          Search
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Substring of correlation ID"
            className="trace-input"
            spellCheck={false}
            autoComplete="off"
            aria-label="Filter correlation IDs"
          />
        </label>
        <label className="wfrun-label">
          Time
          <select
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
            className="wfrun-select"
            aria-label="Time window"
          >
            {WINDOWS.map((w) => (
              <option key={w.label} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>
        </label>
        <label className="wfrun-label">
          Workflow
          <select
            value={workflowFilter}
            onChange={(e) => setWorkflowFilter(e.target.value)}
            className="wfrun-select"
            aria-label="Filter by workflow"
          >
            <option value="">All workflows</option>
            {workflowOptions.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </label>
        <label className="wfrun-label trace-showall">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(e) => setErrorsOnly(e.target.checked)}
            aria-label="Show only traces with errors"
          />
          <span>Errors only</span>
        </label>
      </form>

      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {items.length === 0 && data?.status === 'ok' && (
        <p className="tile-empty">No correlation IDs found in the selected window.</p>
      )}
      {items.length > 0 && (
        <div className="trace-recent">
          {items.map((it) => (
            <RecentRow
              key={it.correlationId}
              item={it}
              selected={selectedCorrelationId === it.correlationId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </TileCard>
  );
}

function RecentRow({
  item,
  selected,
  onSelect,
}: {
  item: RecentCorrelationItem;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const cls = useMemo(() => {
    const c = ['trace-recent-row'];
    if (selected) c.push('trace-recent-row-selected');
    return c.join(' ');
  }, [selected]);
  const status = item.hasError ? { sym: '✗', cls: 'trace-status-fail' } : { sym: '✓', cls: 'trace-status-ok' };
  // Show only the entry-point workflow(s) — exclude syn-shared-* helpers
  // since they're always called downstream and add noise. Fall back to the
  // raw first workflow if (very rarely) only shared workflows are present.
  const entryWorkflows = item.workflows.filter((w) => !w.startsWith('syn-shared-'));
  const display = entryWorkflows.length > 0 ? entryWorkflows : item.workflows;
  const primary = display[0] ?? '—';
  const extra = display.length > 1 ? ` +${display.length - 1}` : '';
  return (
    <button type="button" className={cls} onClick={() => onSelect(item.correlationId)} title={`Workflows: ${item.workflows.join(', ')}`}>
      <span className={`trace-status-pill ${status.cls}`}>{status.sym}</span>
      <span className="trace-recent-id">{item.correlationId}</span>
      <span className="trace-recent-workflow">
        {primary}
        {extra && <span className="muted">{extra}</span>}
      </span>
      <span className="trace-recent-meta">{item.emailFired ? '✉️' : ''}</span>
      <span className="trace-recent-meta">{formatRelative(item.lastSeen)}</span>
    </button>
  );
}
